/** Reading a provisioning QR with the device's camera.
 *
 *  Three platform facts shape this, each checked rather than assumed:
 *
 *  1. `getUserMedia` requires a **secure context**. On `http://192.168.x.x` — the
 *     LAN address this app has been tested on all along — there is no camera at
 *     all. The screen has to say so rather than opening one that fails, and this
 *     only works over HTTPS or `localhost`.
 *  2. `BarcodeDetector` is Chromium-only. The target device is an iPad and iOS
 *     Safari has none, so a JavaScript decoder is required rather than optional.
 *  3. That decoder is **loaded on demand**. It is roughly the size of the entire
 *     rest of the app, and D-4 chose Preact to keep the initial bundle at 13.6KB;
 *     a scanner most sessions never open must not be in it.
 *
 *  What it decodes is what `bujo token new --qr` emits: a compact JSON object,
 *  never a URL. D9a forbids a token in a URL — a fragment is not sent to a
 *  server but it still lands in history, and a scannable link is something
 *  someone can follow by accident.
 */

export interface Provisioning {
  url: string;
  token: string;
}

/** Thrown when the decoder itself could not be fetched, which reads nothing
 *  like a camera fault and must not be reported as one. The chunk is loaded on
 *  demand and is not precached, so this is what an offline first scan gets. */
export class DecoderUnavailable extends Error {
  override name = 'DecoderUnavailable';
}

/** Why scanning is unavailable, in words the screen can show. */
export type ScanBlocked =
  | { reason: 'insecure'; message: string }
  | { reason: 'unsupported'; message: string };

export function scanAvailability(): ScanBlocked | null {
  if (typeof window === 'undefined') return { reason: 'unsupported', message: 'No camera here.' };
  if (!window.isSecureContext) {
    return {
      reason: 'insecure',
      message:
        'Scanning needs a secure context, which a plain http:// address is not. Paste the token, or serve the app over https.',
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { reason: 'unsupported', message: 'This browser exposes no camera.' };
  }
  return null;
}

/** Parse the payload the CLI prints. Returns null for anything else, so a QR
 *  from elsewhere is ignored rather than half-applied. */
export function parseProvisioning(text: string): Provisioning | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const body = parsed as Record<string, unknown>;
  const token = typeof body.token === 'string' ? body.token.trim() : '';
  if (!token) return null;
  // `url` may legitimately be empty: same-origin is the deployed shape.
  const url = typeof body.url === 'string' ? body.url.trim() : '';
  return { url, token };
}

type Decoder = (data: Uint8ClampedArray, width: number, height: number) => string | null;

/** The JavaScript decoder, fetched only when it is actually needed.
 *
 *  Chromium's `BarcodeDetector` is used directly in `scan` instead, because it
 *  reads a video element rather than pixel data and there is no honest way to
 *  wrap both behind one synchronous signature.
 */
async function loadDecoder(): Promise<Decoder> {
  let jsQR;
  try {
    ({ default: jsQR } = await import('jsqr'));
  } catch (error) {
    throw new DecoderUnavailable(error instanceof Error ? error.message : String(error));
  }
  return (data, width, height) => jsQR(data, width, height)?.data ?? null;
}

/** Whether the platform can decode without a download. */
const hasNativeDetector = (): boolean =>
  'BarcodeDetector' in (globalThis as Record<string, unknown>);

/** Read frames from a stream until one contains a provisioning payload.
 *
 *  Returns null when `signal` aborts, so a cancelled scan is not an error.
 */
export async function scan(
  video: HTMLVideoElement,
  signal: AbortSignal,
): Promise<Provisioning | null> {
  const stream = await navigator.mediaDevices.getUserMedia({
    // The back camera on a phone; ignored on a laptop. The resolution is asked
    // for rather than left to the device: a phone will happily hand over
    // 1920x1080, and every one of those pixels is one the decoder has to walk.
    video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
  });

  // Everything past this point is inside the `try`, so the camera is released
  // however this ends. `play()` can reject on a detached element and the
  // decoder import can fail outright — it is fetched on demand and is not
  // precached, so tapping scan while offline lands here. Either one left
  // outside would strand an open camera with the light on and no way to stop it
  // short of a reload.
  try {
    video.srcObject = stream;
    await video.play();

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const native = hasNativeDetector()
      ? new ((globalThis as Record<string, unknown>).BarcodeDetector as new (o: {
          formats: string[];
        }) => { detect: (s: HTMLVideoElement) => Promise<{ rawValue: string }[]> })({
          formats: ['qr_code'],
        })
      : null;
    const fallback = native ? null : await loadDecoder();

    while (!signal.aborted) {
      if (video.videoWidth === 0) {
        await frame();
        continue;
      }
      let text: string | null = null;
      if (native) {
        const found = await native.detect(video).catch(() => []);
        text = found[0]?.rawValue ?? null;
      } else if (context && fallback) {
        // Decode a downscaled copy. At native resolution this walks a
        // megapixel per attempt and the scanner turns sluggish and hot on a
        // phone; DECODE_EDGE is far more detail than a QR needs, since a code
        // has to fill a good part of the frame to be read at all.
        const scale = Math.min(1, DECODE_EDGE / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = context.getImageData(0, 0, canvas.width, canvas.height);
        text = fallback(image.data, image.width, image.height);
      }
      if (text) {
        const parsed = parseProvisioning(text);
        // A QR that is not ours is ignored and scanning continues, rather than
        // failing the whole attempt on someone's wifi sticker.
        if (parsed) return parsed;
      }
      await frame();
    }
    return null;
  } finally {
    for (const track of stream.getTracks()) track.stop();
    video.srcObject = null;
  }
}

/** The longest edge the JS decoder ever sees. */
const DECODE_EDGE = 640;

const frame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
